import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    Text,
    Box,
    Spinner,
    Flex,
    Button,
    Checkbox,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const DemoVideo = ({ isOpen, onClose }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [disableVideo, setDisableVideo] = useState(false); // Checkbox state

    // Track events with Google Analytics
    const trackEvent = (action, label, additionalData = {}) => {
        if (window.gtag) {
            window.gtag('event', action, {
                event_category: 'Demo Video',
                event_label: label,
                ...additionalData,
                non_interaction: false,
            });
        }
    };

    const handleVideoLoad = () => {
        setIsLoading(false);
        trackEvent('Video Loaded', 'Pabs Product Walkthrough', { timestamp: Date.now() });
    };

    const handleVideoPlay = (currentTime) => {
        trackEvent('Video Played', 'Pabs Product Walkthrough', { current_time: currentTime });
    };

    const handleVideoPause = (currentTime) => {
        trackEvent('Video Paused', 'Pabs Product Walkthrough', { current_time: currentTime });
    };

    const handleVideoEnded = () => {
        trackEvent('Video Completed', 'Pabs Product Walkthrough', { timestamp: Date.now() });
        handleClose(); // Close modal when video ends
    };

    const handleDisableVideo = async () => {
        try {
            console.log('Updating demo status to:', disableVideo);
            const response = await fetchWithAuth(`/api/user/update-demo-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ hasSeenDemo: disableVideo }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to update demo status:', errorText);
                throw new Error('Failed to update demo status');
            }

            console.log('Demo status updated successfully');
            localStorage.setItem('hasSeenDemo', 'true'); // Backup client-side flag
            trackEvent('Demo Status Updated', disableVideo ? 'Opted Out' : 'Opted In');
        } catch (error) {
            console.error('Error updating demo status:', error);
        }
    };

    const handleClose = () => {
        if (disableVideo) {
            handleDisableVideo(); // Update the server flag when the user closes the modal
        }
        trackEvent('Modal Closed', 'User Closed the Demo Video');
        onClose(); // Close the modal
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="16px" bg="brand.800" overflow="hidden" p="0">
                <Box position="relative" overflow="hidden">
                    {isLoading && (
                        <Flex
                            justify="center"
                            align="center"
                            position="absolute"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            bg="rgba(255, 255, 255, 0.6)"
                            zIndex="10"
                        >
                            <Spinner size="lg" color="white" />
                        </Flex>
                    )}
                    <video
                        controls
                        width="100%"
                        style={{ display: 'block' }}
                        onCanPlayThrough={handleVideoLoad}
                        onPlay={(e) => handleVideoPlay(e.target.currentTime)}
                        onPause={(e) => handleVideoPause(e.target.currentTime)}
                        onEnded={handleVideoEnded}
                    >
                        <source src="/videos/pabs-product-walkthrough-2025-01-06.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </Box>
                <ModalBody p={6}>
                    <Text mt={2} textAlign="left" fontSize="xl" fontWeight="bold" color="white">
                    🎉 Welcome to Pabs, watch a quick video introduction
                    </Text>
                    <Text textAlign="left" fontSize="lg" color="brand.400" >
                    Pabs provides design strategy support, just like having your own design lead! Explore step-by-step UX and design approaches, activity scheduling, and so much more. <br/> I highly recommend watching the video—you'll discover just how much you can achieve with Pabs in its beta version!
                    </Text>
                </ModalBody>
                <Flex justify="flex-end" alignItems="center" p={6}>
                <Checkbox
                        colorScheme="white"
                        isChecked={disableVideo}
                        onChange={(e) => {
                            setDisableVideo(e.target.checked);
                            trackEvent('Checkbox Toggled', e.target.checked ? 'Disable Video' : 'Enable Video');
                        }}
                        mr={10}
                        color="white"
                        fontSize="sm"
                    >
                        I've seen the video and I understand how Pabs works, don’t show the video again
                    </Checkbox>
                    <Button
                        onClick={handleClose}
                        colorScheme="blue"
                        variant="solid"
                        borderRadius="md"
                        p="6"
                    >
                        Let's go
                    </Button>
                </Flex>
            </ModalContent>
        </Modal>
    );
};

export default DemoVideo;
